import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import Discipline from '../components/Discipline';
import { isVacantSv } from '../components/SvManage';
import { mskToday } from '../lib/alerts';
import ReferralBoard from '../components/ReferralBoard';
import TasksTab from '../components/TasksTab';
import TaskBanners from '../components/TaskBanners';
import useTaskBadge from '../hooks/useTaskBadge';
import { hashTaskId } from '../lib/tasks';
import StaffPlanSummary from '../components/StaffPlanSummary';
import { Topbar, NavTabs, Card, SectionTitle, Loader, Btn } from '../components/UI';
import { supabase } from '../lib/supabase';
import { getActivePeriod, setStatus, clearStatus, setVacation, deleteVacation, closeVacancyByRoute, approveVacation, rejectVacation } from '../lib/data';
import EditableTabell from '../components/EditableTabell';
import DailyStatsPanel from '../components/DailyStatsPanel';
import { useRecruiting } from '../lib/recruiting';
import { rememberRoutes, canHideRoutes, hideRoute } from '../lib/data';
import HiddenRoutes from '../components/HiddenRoutes';
import { getPeriodDataForRoutes, getRouteWeekAgg, getRouteWeekAggTo, buildRouteWeekAggRaw, aggregateWeeks, getHyperDataForRoutes, aggregateHyper, getVisitFactsByRoutes, getRowsByRouteChunks, getUnplannedVacations, getVacationsOverlappingPeriod, getRouteDayForecast } from '../lib/data';
import ForecastPivot from '../components/ForecastPivot';
import HyperPivot from '../components/HyperPivot';
import NoPlanTree from '../components/NoPlanTree';
import AlertRibbon, { GoBtn } from '../components/AlertRibbon';
import QualityPlanning from '../components/QualityPlanning';
import { routeNoEntry, buildNoForecastAlerts, buildRecruitAlerts, buildAlerts, buildPlanAlerts, sortAlerts, groupAlerts, partialInfo, mergePartial } from '../lib/alerts';

// Точки-гипперы СВ, сведённые по ship_to
function pointGroupsForRoutes(routes, hdata) {
  const byShip = {};
  routes.forEach(r => (hdata.pointsByRoute?.[r.id] || []).forEach(p => {
    const key = String(p.ship_to);
    if (!byShip[key]) byShip[key] = { ship_to: p.ship_to, client: p.client, address: p.address, pointIds: [] };
    byShip[key].pointIds.push(p.id);
    if (p.client && !byShip[key].client) byShip[key].client = p.client;
    if (p.address && !byShip[key].address) byShip[key].address = p.address;
  }));
  return Object.values(byShip);
}
function svHyperNode(sv, routes, hdata, buKey) {
  const groups = pointGroupsForRoutes(routes, hdata);
  const children = groups.map(gr => ({ key: `hp:${sv.id}:${gr.ship_to}`, label: gr.client || '—', sub: String(gr.ship_to), addr: gr.address || null, pointKey: `${sv.id}:${gr.ship_to}`, pointIds: gr.pointIds }));
  return { key: `hsv:${buKey}:${sv.id}`, label: sv.full_name, pointIds: children.flatMap(c => c.pointIds), children };
}
function buildRmHyperRoots(tmData, hdata) {
  const buOf = g => g.sv.bu || 'Без BU';
  const buSet = Array.from(new Set(tmData.flatMap(t => t.svGroups.map(buOf)))).sort();
  return buSet.map(bu => {
    const tmNodes = tmData.map(t => {
      const svs = t.svGroups.filter(g => buOf(g) === bu);
      const svNodes = svs.map(g => svHyperNode(g.sv, g.routes, hdata, bu)).filter(n => n.children.length);
      if (!svNodes.length) return null;
      return { key: `htm:${bu}:${t.tm.id}`, label: t.tm.full_name, pointIds: svNodes.flatMap(n => n.pointIds), children: svNodes };
    }).filter(Boolean);
    return { key: `hbu:${bu}`, label: bu, pointIds: tmNodes.flatMap(n => n.pointIds), children: tmNodes };
  }).filter(n => n.children.length);
}
// Точки без планирования: заказ есть, план по периоду = 0
function hyperUnplanned(roots, weeks, weekNums, daily) {
  const dates = weekNums.flatMap(w => (weeks[w]?.days || []).map(d => d.date));
  const out = new Set();
  const walk = node => {
    if (node.pointKey) {
      let ord = 0, plan = 0;
      node.pointIds.forEach(pid => dates.forEach(dt => { const c = daily[pid]?.[dt]; if (!c) return; ord += c.order || 0; if (c.plan != null) plan += c.plan; }));
      if (ord > 0 && plan === 0) out.add(node.pointKey);
    }
    (node.children || []).forEach(walk);
  };
  roots.forEach(walk);
  return out;
}

// Дерево для сводной прогноза: BU → ТМ → СВ → Маршрут
function mrcName(r) { const m = r.merchandisers; const x = Array.isArray(m) ? m[0] : m; return (r.is_vacancy || !x?.full_name) ? `Вакансия (${r.internal_code})` : x.full_name; }
function buildRmForecastRoots(tmData) {
  const buOf = g => g.sv.bu || 'Без BU';
  const buSet = Array.from(new Set(tmData.flatMap(t => t.svGroups.map(buOf)))).sort();
  return buSet.map(bu => {
    const tmNodes = tmData.map(t => {
      const svs = t.svGroups.filter(g => buOf(g) === bu);
      if (!svs.length) return null;
      const children = svs.map(g => ({
        key: `sv:${bu}:${g.sv.id}`, label: g.sv.full_name, routes: g.routes,
        children: g.routes.map(r => ({ key: `rt:${r.id}`, label: mrcName(r), sub: [r.internal_code, r.external_code].filter(Boolean).join(' · '), routes: [r], routeId: r.id })),
      }));
      return { key: `tm:${bu}:${t.tm.id}`, label: t.tm.full_name, routes: svs.flatMap(g => g.routes), children };
    }).filter(Boolean);
    return { key: `bu:${bu}`, label: bu, routes: tmNodes.flatMap(n => n.routes), children: tmNodes };
  });
}
import { datesFor, weeksFor, DASH_CSS, KpiTiles, DiscTiles, HyperTiles, RouteMetricRow, HyperMetricRow, weeklySeries, weeklyHyperSeries, DynamicsChart, WeekStrip, ScopeTabs, TriggersCard } from '../components/DashParts';
import { downloadRmSummary, downloadForecastRm } from '../lib/summaryExport';

const WEEK_COLORS = {
  36: { hdr: '#2E75B6', light: '#D6E4F0' },
  37: { hdr: '#70AD47', light: '#E2EFDA' },
  38: { hdr: '#FFC000', light: '#FFF2CC' },
  39: { hdr: '#F4B183', light: '#FCE4D6' },
  40: { hdr: '#808080', light: '#EDEDED' },
};

const OVERLAP = new Set(['БЛ+МР','БЛ+ПМ','БЛ+ВЫХ','ОТ+МР','ОТ+ПМ','ОТ+ВЫХ','ДО+МР','ДО+ПМ','ДО+ВЫХ','ВК+МР','ВК+ПМ','ВК+ВЫХ']);

function calcMetrics(statuses, routeId, days) {
  let exits=0, sick=0, vacation=0, dayoff=0, vacancy=0, overlaps=0;
  days.forEach(({ date }) => {
    const s = statuses[routeId]?.[date];
    if (!s) return;
    if (s==='1'||s==='СВ') exits++;
    if (s==='БЛ-') sick++;
    if (s==='ОТ-') vacation++;
    if (s==='ДО-') dayoff++;
    if (s==='ВК-') vacancy++;
    if (OVERLAP.has(s)) overlaps++;
  });
  return { exits, sick, vacation, dayoff, vacancy, overlaps };
}

// Суммарные метрики за неделю по набору routes/statuses
function weekTotals(svGroups, w, weeks) {
  return svGroups.reduce((acc, g) => {
    g.routes.forEach(r => {
      const m = calcMetrics(g.statuses, r.id, weeks[w]?.days || []);
      acc.exits += m.exits; acc.sick += m.sick; acc.vacation += m.vacation;
      acc.dayoff += m.dayoff; acc.overlaps += m.overlaps;
    });
    return acc;
  }, { exits: 0, sick: 0, vacation: 0, dayoff: 0, overlaps: 0 });
}

export default function RmPage() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState(() => hashTaskId() ? 5 : 0);
  const [taskN, refreshTaskN] = useTaskBadge(user.id);
  const [period, setPeriod] = useState(null);
  const [weeks, setWeeks]   = useState({});
  const [weekNums, setWeekNums] = useState([]);
  const [tmData, setTmData] = useState([]); // [{ tm, svGroups: [{ sv, routes, statuses }] }]
  const [vacations, setVacations] = useState([]); // все отпуска проекта
  const [visitFacts, setVisitFacts] = useState([]);
  const [servicePct, setServicePct] = useState({}); // { route_id: { week_number: pct } }
  const [rweeks, setRweeks] = useState({});
  const [hdata, setHdata] = useState({ pointsByRoute: {}, daily: {} });
  const [otAlert, setOtAlert] = useState({ byRoute: {}, total: 0, routes: 0 });
  const [loading, setLoading] = useState(true);
  const recruiting = useRecruiting(); // заявки подбора для алёртов

  // Drill-down: null = список ТМ, tmId = список СВ этого ТМ, {tmId, svId} = редактирование СВ
  const [selectedTmId, setSelectedTmId] = useState(null);
  const [selectedSvId, setSelectedSvId] = useState(null);
  const [dashPeriodSel, setDashPeriodSel] = useState('month'); // #3 — сохранять выбор недели дашборда между вкладками
  const [focusRouteId, setFocusRouteId] = useState(null); // переход из алёрта «нет планирования» в сводную
  const [fcMode, setFcMode] = useState('routes'); // сводная: маршруты / гипперы
  const [focusHyperKey, setFocusHyperKey] = useState(null);
  const frozenMode = false; // заморозка убрана // раздел «Прогноз»: заморожено (по умолч.) / актуально
  const [rweeksFrozen, setRweeksFrozen] = useState({});
  const [partialQ, setPartialQ] = useState(null); // идущая неделя «на вчера»
  const [hyperFrozenDaily, setHyperFrozenDaily] = useState({});
  const [freezeAt, setFreezeAt] = useState(null);
  const [lastSeen, setLastSeen] = useState({});
  const [personalForecasts, setPersonalForecasts] = useState({}); // { sv_id: { week: pct } }
  const [navFocus, setNavFocus] = useState(null);

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    try {
      setLoading(true);
      const p = await getActivePeriod();
      setPeriod(p);

      const wks = {};
      (p.work_weeks || []).forEach(w => {
        const days = [];
        const from = new Date(w.date_from), to = new Date(w.date_to);
        for (let d = new Date(from); d <= to; d.setDate(d.getDate()+1)) {
          days.push({ day: d.getDate(), dow: d.getDay(), date: d.toISOString().split('T')[0] });
        }
        wks[w.week_number] = { ...w, days };
      });
      setWeeks(wks);
      const wn = Object.keys(wks).map(Number).sort((a,b)=>a-b);
      setWeekNums(wn);

      const { data: tms } = await supabase.from('users').select('*').eq('role', 'tm').eq('is_active', true).order('full_name');

      const allTmData = await Promise.all((tms || []).map(async tm => {
        const { data: svList } = await supabase
          .from('supervisors')
          .select('id, full_name, internal_code, bu, user_id')
          .eq('tm_id', tm.id)
          .eq('is_active', true);

        const svGroups = await Promise.all((svList || []).map(async sv => {
          const { data: routes } = await supabase
            .from('routes')
            .select('*, merchandisers(*)')
            .eq('sv_id', sv.id)
            .eq('is_active', true)
          .neq('is_hidden', true);

          const allR = routes || [];
          const routeIds = allR.filter(r => !r.is_pm).map(r => r.id);
          let statusMap = {};
          if (routeIds.length) {
            const statusList = await getRowsByRouteChunks('daily_statuses', 'route_id, work_date, status_code', p.id, routeIds);
            (statusList || []).forEach(s => {
              if (!statusMap[s.route_id]) statusMap[s.route_id] = {};
              statusMap[s.route_id][s.work_date] = s.status_code;
            });
          }
          return { sv, routes: allR.filter(r => !r.is_pm), pmRoutes: allR.filter(r => r.is_pm), statuses: statusMap };
        }));

        return { tm, svGroups };
      }));

      allTmData.forEach(t => (t.svGroups || []).forEach(g => rememberRoutes(g.routes)));
      setTmData(allTmData);

      // Последний вход СВ и личные прогнозы SL (для алёртов и качества планирования)
      try {
        const svAll = allTmData.flatMap(t => t.svGroups.map(g => g.sv));
        const uids = svAll.map(x => x.user_id).filter(Boolean);
        const m = {};
        for (let i = 0; i < uids.length; i += 100) {
          const { data: us, error: ue } = await supabase.from('users').select('id, last_seen_at').in('id', uids.slice(i, i + 100));
          if (ue) break;
          (us || []).forEach(u => { if (u.last_seen_at) m[u.id] = u.last_seen_at; });
        }
        setLastSeen(m);
        const { getSvPersonalForecastsBySv } = await import('../lib/data');
        const fc = await getSvPersonalForecastsBySv(p.id, svAll.map(x => x.id));
        const fMap = {};
        (fc || []).forEach(f => { (fMap[f.supervisor_id] ||= {})[f.week_number] = f.forecast_pct; });
        setPersonalForecasts(fMap);
      } catch (e) { console.warn('rm alerts extra', e?.message || e); }

      // Все отпуска проекта
      const allRouteIds = allTmData.flatMap(t => t.svGroups.flatMap(g => g.routes.map(r => r.id)));
      if (allRouteIds.length) {
        const vacs = await getVacationsOverlappingPeriod(p.id, [...allRouteIds, ...allTmData.flatMap(t => t.svGroups.flatMap(g => (g.pmRoutes || []).map(r => r.id)))]);
        setVacations(vacs || []);

        // Триггер: ОТ в табеле без подтверждённого отпуска
        const otu = await getUnplannedVacations(p.id, allRouteIds, vacs || []);
        setOtAlert(otu);

        const facts = await getVisitFactsByRoutes(p.id, allRouteIds);
        setVisitFacts(facts);

        // Service% по всем маршрутам проекта (для SL% в «Итогах»)
        const svcData = await getRowsByRouteChunks('service_pct', 'route_id, week_number, service_pct', p.id, allRouteIds);
        const svcMap = {};
        (svcData || []).forEach(s => {
          if (!svcMap[s.route_id]) svcMap[s.route_id] = {};
          svcMap[s.route_id][s.week_number] = s.service_pct;
        });
        setServicePct(svcMap);

        // Свёртка маршрут×неделя из БД (оптимизировано). Фолбэк — сырые данные.
        const allRoutes = allTmData.flatMap(t => t.svGroups.flatMap(g => g.routes));
        let rw;
        try {
          rw = await getRouteWeekAgg(p.id);
          if (!rw || !Object.keys(rw).length) throw new Error('empty rpc');
        } catch (e) {
          console.warn('[РМ] RPC dash_route_week недоступна, фолбэк на сырые данные:', e?.message || e);
          const pdMerged = { orders: {}, statuses: {}, facts: {} };
          for (let i = 0; i < allRouteIds.length; i += 150) {
            const pd = await getPeriodDataForRoutes(p.id, allRouteIds.slice(i, i + 150));
            Object.assign(pdMerged.orders, pd.orders);
            Object.assign(pdMerged.statuses, pd.statuses);
            Object.assign(pdMerged.facts, pd.facts);
          }
          rw = buildRouteWeekAggRaw(allRoutes, wks, wn, pdMerged);
        }
        setRweeks(rw);

        // Гипперы по всем маршрутам проекта
        const hdMerged = { pointsByRoute: {}, daily: {} };
        for (let i = 0; i < allRouteIds.length; i += 150) {
          const hd = await getHyperDataForRoutes(p.id, allRouteIds.slice(i, i + 150));
          Object.assign(hdMerged.pointsByRoute, hd.pointsByRoute);
          Object.assign(hdMerged.daily, hd.daily);
        }
        setHdata(hdMerged);

        // Заморозка прогноза убрана: везде живые данные.
        try {
          const pi = partialInfo(wks, wn);
          if (pi) {
            const pl = await getRouteWeekAggTo(p.id, false, null, pi.until);
            setPartialQ({ ...pi, frozen: pl, live: pl });
          }
        } catch (e) { console.warn('partial week', e?.message || e); }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  // Средний SL% по набору групп СВ за неделю
  function weekServiceAvg(svGroups, w) {
    const pcts = svGroups.flatMap(g => g.routes.filter(r => !r.is_vacancy).map(r => servicePct[r.id]?.[w])).filter(p => p != null);
    return pcts.length ? (pcts.reduce((a, b) => a + b, 0) / pcts.length) : null;
  }
  function routesCount(svGroups) {
    return svGroups.reduce((s, g) => s + g.routes.filter(r => !r.is_vacancy).length, 0);
  }

  // Алёрты по всем ТМ проекта
  const rmAlerts = sortAlerts(tmData.flatMap(({ tm, svGroups }) => [
    ...buildAlerts({ svGroups, vacations, weeks, weekNums, lastSeen, tmId: tm.id, tmName: tm.full_name }),
    ...buildPlanAlerts({ svGroups, weeks, weekNums, rwFrozen: rweeks, rwLive: rweeks, forecasts: personalForecasts, tmId: tm.id, tmName: tm.full_name }),
    ...buildNoForecastAlerts({ svGroups, weeks, weekNums, rweeks, vacations, tmId: tm.id, tmName: tm.full_name }),
    ...buildRecruitAlerts({ svGroups, recruiting, tmId: tm.id, tmName: tm.full_name }),
  ]));
  // «Перейти»: ТМ → СВ → нужная неделя / маршрут / отпуск
  function goToAlert(a) {
    const g = a.goto || {};
    if (g.top === 'forecast') { setTab(2); setSelectedTmId(null); setSelectedSvId(null); return; }
    setTab(0);
    setSelectedTmId(a.tmId);
    setSelectedSvId(g.svId || null);
    setNavFocus(g.svId ? { ...g, key: Date.now() } : null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  if (loading) return <Loader text="Загружаем проект..." />;

  const selectedTm = tmData.find(t => t.tm.id === selectedTmId);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Topbar
        title={user.full_name}
        subtitle="Региональный менеджер — весь проект"
        rightContent={
          <button onClick={logout} style={{ background: 'rgba(255,255,255,.12)', border: 'none', color: 'white', padding: '6px 12px', borderRadius: 6, fontSize: 12 }}>Выйти</button>
        }
      />
      <NavTabs tabs={['Все ТМ', 'Проект целиком', 'Прогноз', 'Дисциплина СВ', 'Приведи друга', { label: 'Мои задачи', badge: taskN }]} active={tab} onChange={(t) => { setTab(t); setSelectedTmId(null); setSelectedSvId(null); }} />

      <div style={{ padding: 16, maxWidth: 800, margin: '0 auto', width: '100%' }}>
        {tab !== 5 && <TaskBanners userId={user.id} refreshKey={taskN} onOpen={() => setTab(5)} />}
        {tab === 5 && <TasksTab user={user} onChanged={refreshTaskN} />}

        {tab !== 5 && !selectedTmId && (
          <>
            <SectionTitle>{period?.name} — накопительно по всему проекту</SectionTitle>
          </>
        )}

        {/* ── УРОВЕНЬ 1: СПИСОК ТМ ── */}
        {tab === 0 && !selectedTmId && (
          <>
            {tmData.map(({ tm, svGroups }) => {
              const tmTotal = weekNums.reduce((s, w) => s + weekTotals(svGroups, w, weeks).exits, 0);
              const svCount = svGroups.length;
              const mrcCount = svGroups.reduce((s, g) => s + g.routes.length, 0);

              return (
                <Card key={tm.id} style={{ cursor: 'pointer' }}>
                  <div onClick={() => setSelectedTmId(tm.id)} style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{tm.full_name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{svCount} СВ · {mrcCount} маршрутов</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 24, fontWeight: 700, color: 'var(--accent)' }}>{tmTotal}</span>
                      <span style={{ color: 'var(--text-light)', fontSize: 18 }}>→</span>
                    </div>
                  </div>
                  {(() => {
                    const mine = rmAlerts.filter(x => x.tmId === tm.id);
                    if (!mine.length) return null;
                    return (
                      <div style={{ padding: '0 16px 10px', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                        {groupAlerts(mine).map(gr => {
                          const bad = gr.items.some(x => x.level === 'bad');
                          return (
                            <span key={gr.label} onClick={() => goToAlert(gr.items[0])} title="Перейти"
                              style={{ cursor: 'pointer', fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: bad ? 'var(--red-bg)' : '#FFF7E0', color: bad ? 'var(--red)' : '#8A6100', border: bad ? 'none' : '1px solid #F0C56A' }}>
                              {gr.label} · {gr.items.length}
                            </span>
                          );
                        })}
                        <span style={{ marginLeft: 'auto' }}><GoBtn small onClick={() => goToAlert(mine[0])} /></span>
                      </div>
                    );
                  })()}
                  <div style={{ padding: '0 16px 12px', display: 'flex', gap: 6 }}>
                    {weekNums.map(w => {
                      const t = weekTotals(svGroups, w, weeks);
                      const c = WEEK_COLORS[w] || WEEK_COLORS[36];
                      const sl = weekServiceAvg(svGroups, w);
                      return (
                        <div key={w} style={{ flex: 1, textAlign: 'center', padding: '6px 4px', borderRadius: 6, background: c.light }}>
                          <div style={{ fontSize: 9, fontWeight: 700, color: c.hdr }}>W{w}</div>
                          <div style={{ fontSize: 15, fontWeight: 700 }}>{t.exits}</div>
                          <div style={{ fontSize: 8, color: 'var(--text-light)' }}>из {routesCount(svGroups)}</div>
                          <div style={{ fontSize: 8, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.5 }}>
                            {t.sick > 0 && <div style={{ color: 'var(--red)' }}>БЛ:{t.sick}</div>}
                            {t.vacation > 0 && <div style={{ color: '#7D5A00' }}>ОТ:{t.vacation}</div>}
                            {t.dayoff > 0 && <div style={{ color: '#843C0C' }}>ДО:{t.dayoff}</div>}
                            {t.overlaps > 0 && <div style={{ color: '#276221' }}>↔{t.overlaps}</div>}
                          </div>
                          {sl != null && <div style={{ fontSize: 9, fontWeight: 700, color: c.hdr, marginTop: 2 }}>SL:{(sl*100).toFixed(0)}%</div>}
                        </div>
                      );
                    })}
                  </div>
                </Card>
              );
            })}
          </>
        )}

        {/* ── УРОВЕНЬ 2: СПИСОК СВ ВЫБРАННОГО ТМ ── */}
        {tab === 0 && selectedTmId && !selectedSvId && selectedTm && (
          <>
            <button onClick={() => setSelectedTmId(null)} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: 'var(--accent)', fontSize: 13, fontWeight: 600, marginBottom: 12, cursor: 'pointer' }}>
              ← Все ТМ
            </button>
            <SectionTitle>{selectedTm.tm.full_name}</SectionTitle>

            {selectedTm.svGroups.map(({ sv, routes, statuses }) => {
              const totalExits = routes.reduce((s, r) => {
                weekNums.forEach(w => { s += calcMetrics(statuses, r.id, weeks[w]?.days || []).exits; });
                return s;
              }, 0);
              const routeIds = routes.map(r => r.id);
              const pendingCount = vacations.filter(v => v.status === 'pending' && routeIds.includes(v.route_id)).length;

              return (
                <Card key={sv.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedSvId(sv.id)}>
                  <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{sv.full_name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{routes.length} маршрутов</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {pendingCount > 0 && <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: '#FFFDE7', color: '#7D5A00', border: '1px solid #F9A825' }}>📅 {pendingCount}</span>}
                      <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--accent)' }}>{totalExits}</span>
                      <span style={{ color: 'var(--text-light)' }}>→</span>
                    </div>
                  </div>
                </Card>
              );
            })}
          </>
        )}

        {/* ── УРОВЕНЬ 3: РЕДАКТИРОВАНИЕ КОНКРЕТНОГО СВ ── */}
        {tab === 0 && selectedTmId && selectedSvId && selectedTm && (
          <RmSvEditor
            svId={selectedSvId}
            svGroups={selectedTm.svGroups}
            vacations={vacations}
            period={period}
            weeks={weeks}
            weekNums={weekNums}
            rmUserId={user.id}
            onBack={() => { setSelectedSvId(null); setNavFocus(null); }}
            focus={navFocus}
            onUpdateGroups={(updater) => {
              setTmData(prev => prev.map(t => t.tm.id === selectedTmId ? { ...t, svGroups: updater(t.svGroups) } : t));
            }}
            onUpdateVacations={setVacations}
            visitFacts={visitFacts}
          />
        )}

        {/* ── ПРОЕКТ ЦЕЛИКОМ ── */}
        {tab === 4 && <ReferralBoard mode="rm" userId={user.id} />}
        {tab === 3 && (
          <Discipline period={period} weeks={period?.work_weeks || []}
            units={tmData.map(({ tm, svGroups }) => {
              const kids = svGroups.filter(g => !isVacantSv(g.sv)).map(g => ({ id: g.sv.id, label: g.sv.full_name, sub: `${g.routes.length} маршрутов`, svIds: [g.sv.id] }));
              return { id: tm.id, label: tm.full_name, sub: `${kids.length} СВ`, svIds: kids.flatMap(k => k.svIds), children: kids };
            })} title="Дисциплина СВ по ТМ" />
        )}

        {tab === 1 && (
          <>
          <RmDashboard tmData={tmData} weeks={weeks} weekNums={weekNums} rweeks={rweeks} hdata={hdata} otAlert={otAlert} periodSel={dashPeriodSel} setPeriodSel={setDashPeriodSel} alerts={rmAlerts} onGo={goToAlert} />

          <DailyStatsPanel
            weeks={weeks}
            weekNums={weekNums}
            title="По дням"
            groupLevels={[
              { key: 'all', label: 'Весь проект' },
              { key: 'bu',  label: 'По BU' },
              { key: 'tm',  label: 'По ТМ' },
            ]}
            getGroups={(levelKey) => {
              if (levelKey === 'all') return [{ id: null, label: null }];
              if (levelKey === 'tm') {
                return tmData.map(({ tm }) => ({ id: tm.id, label: tm.full_name }));
              }
              if (levelKey === 'bu') {
                const buSet = new Set();
                tmData.forEach(({ svGroups }) => svGroups.forEach(({ sv }) => buSet.add(sv.bu || 'Без BU')));
                return Array.from(buSet).sort().map(bu => ({ id: bu, label: bu }));
              }
              return [{ id: null, label: null }];
            }}
            getDayMetricsFor={(date, levelKey, groupId) => {
              const acc = { exits: 0, sick: 0, vacation: 0, dayoff: 0, overlaps: 0 };
              tmData.forEach(({ tm, svGroups }) => {
                if (levelKey === 'tm' && tm.id !== groupId) return;
                svGroups.forEach(({ sv, routes, statuses }) => {
                  if (levelKey === 'bu' && (sv.bu || 'Без BU') !== groupId) return;
                  routes.filter(r => !r.is_vacancy).forEach(r => {
                    const s = statuses[r.id]?.[date];
                    if (!s) return;
                    if (s==='1'||s==='СВ') acc.exits++;
                    if (s==='БЛ-') acc.sick++;
                    if (s==='ОТ-') acc.vacation++;
                    if (s==='ДО-') acc.dayoff++;
                    if (OVERLAP.has(s)) acc.overlaps++;
                  });
                });
              });
              return acc;
            }}
          />
          </>
        )}

        {tab === 2 && (() => {
          const rweeksUse = rweeks;
          const hyperDailyUse = hdata.daily || {};
          const roots = buildRmForecastRoots(tmData);
          const todayStr = mskToday();
          const curWeek = weekNums.find(w => weeks[w] && weeks[w].date_from <= todayStr && todayStr <= weeks[w].date_to) ?? null;
          const unplanned = new Set();
          tmData.forEach(t => t.svGroups.forEach(g => g.routes.forEach(r => {
            if (r.is_vacancy) return;
            let plan = 0, ord = 0;
            weekNums.forEach(w => { const c = rweeksUse[r.id]?.[w]; if (c) { plan += c.plan || 0; ord += c.ord || 0; } });
            const cw = curWeek != null ? rweeksUse[r.id]?.[curWeek] : null;
            const curZero = !!cw && (cw.ord || 0) > 0 && (cw.plan || 0) === 0;
            const noEntry = ord > 0 && !isVacantSv(g.sv) && routeNoEntry(g.statuses?.[r.id], curWeek != null ? weeks[curWeek] : null, todayStr) && !vacations.some(v => v.route_id === r.id);
            if ((ord > 0 && plan === 0) || curZero || noEntry) unplanned.add(r.id);
          })));
          const hRoots = buildRmHyperRoots(tmData, hdata);
          const hUnplanned = hyperUnplanned(hRoots, weeks, weekNums, hyperDailyUse);
          return (
          <>
            <QualityPlanning
              units={tmData.map(({ tm, svGroups }) => ({ id: tm.id, label: tm.full_name, sub: `${svGroups.length} СВ`, groups: svGroups }))}
              unitTitle="ТМ" weeks={weeks} weekNums={weekNums}
              rwFrozen={partialQ ? mergePartial(rweeks, partialQ.live, partialQ.week) : rweeks}
              rwLive={partialQ ? mergePartial(rweeks, partialQ.live, partialQ.week) : rweeks}
              partial={partialQ} forecasts={personalForecasts} 
              onGo={(tmId) => { setTab(0); setSelectedTmId(tmId); setSelectedSvId(null); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
            <SectionTitle>Планирование кадров</SectionTitle>
            <StaffPlanSummary mode="rm" />
            <SectionTitle>Прогноз — сводная</SectionTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
              <div style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                {[['routes', 'Маршруты'], ['hyper', 'Гипперы']].map(([m, l]) => (
                  <button key={m} onClick={() => setFcMode(m)} style={{ border: 0, background: fcMode === m ? 'var(--accent)' : 'white', color: fcMode === m ? 'white' : 'var(--text-muted)', fontSize: 12, fontWeight: 600, padding: '7px 16px', cursor: 'pointer' }}>{l}</button>
                ))}
              </div>
            </div>
            {fcMode === 'routes' ? (
              <>
                {unplanned.size > 0 && <NoPlanTree roots={roots} unplannedSet={unplanned} onPick={setFocusRouteId} leafId={(n) => n.routeId ?? null} unit="маршр." />}
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>BU → ТМ → СВ → Маршрут. Ячейка = Прогноз/Заказ + %. Клик по маршруту — прогноз по дням. Данные живые.</div>
                <ForecastPivot roots={roots} weekNums={weekNums} rweeks={rweeksUse} weeks={weeks} srcKey={frozenMode}
                  loadRouteDays={(routeId) => getRouteDayForecast(period.id, routeId, frozenMode)} focusRouteId={focusRouteId} />
              </>
            ) : (
              <>
                {hUnplanned.size > 0 && <NoPlanTree roots={hRoots} unplannedSet={hUnplanned} onPick={setFocusHyperKey} leafId={(n) => n.pointKey ?? null} unit="точек" />}
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>BU → ТМ → СВ → Точка (ship_to). Ячейка = Планир./Заказ + %. Клик по точке — планирование по дням. Живые данные.</div>
                <HyperPivot roots={hRoots} weekNums={weekNums} weeks={weeks} daily={hyperDailyUse} focusKey={focusHyperKey} />
              </>
            )}
          </>
          );
        })()}

      </div>
    </div>
  );
}

// ── ДАШБОРД РМ: проект → ТМ → СВ (Показатели / Дисциплина) ────────────────────
function RmDashboard({ tmData, weeks, weekNums, rweeks, hdata, otAlert, periodSel, setPeriodSel, alerts = [], onGo }) {
  const [view, setView] = useState('kpi');
  const [openTm, setOpenTm] = useState(null);
  const [buSel, setBuSel] = useState('all');   // фильтр по BU (регион) — весь дашборд
  const [tmSel, setTmSel] = useState('all');   // срез графика по ТМ внутри BU
  const dates = datesFor(periodSel, weeks, weekNums);       // для гиппера
  const selWeeks = weeksFor(periodSel, weekNums);           // для показателей/дисциплины
  const isH = view === 'hyper';
  const Tiles = view === 'kpi' ? KpiTiles : view === 'disc' ? DiscTiles : HyperTiles;
  const agg = (routes) => isH ? aggregateHyper(routes, dates, hdata) : aggregateWeeks(routes, selWeeks, rweeks);

  // Список BU из супервизоров
  const buList = Array.from(new Set(tmData.flatMap(t => t.svGroups.map(g => g.sv.bu || 'Без BU')))).sort();
  // Фильтруем весь проект по выбранному BU (оставляем только СВ этого BU и ТМ, где такие СВ есть)
  const buTmData = (buSel === 'all'
    ? tmData
    : tmData.map(t => ({ ...t, svGroups: t.svGroups.filter(g => (g.sv.bu || 'Без BU') === buSel) })).filter(t => t.svGroups.length));
  // Если выбранного ТМ нет в текущем BU — сбрасываем срез
  const tmSelValid = tmSel === 'all' || buTmData.some(t => t.tm.id === tmSel);
  const effTmSel = tmSelValid ? tmSel : 'all';

  const allRoutes = buTmData.flatMap(t => t.svGroups.flatMap(g => g.routes));
  const projAgg = agg(allRoutes);

  const scopedTm = effTmSel !== 'all' ? buTmData.find(t => t.tm.id === effTmSel) : null;
  const chartRoutes = scopedTm ? scopedTm.svGroups.flatMap(g => g.routes) : allRoutes;
  const chartLabel = scopedTm ? scopedTm.tm.full_name : (buSel === 'all' ? 'весь проект' : buSel);
  const activeRoutes = chartRoutes.filter(r => !r.is_vacancy).length;
  const wser = isH ? weeklyHyperSeries(chartRoutes, weeks, weekNums, hdata) : weeklySeries(chartRoutes, weekNums, rweeks);
  const chartSeries = view === 'kpi'
    ? [{ key: 'sl', name: 'SL Факт/Заказ', color: '#2a78d6' }, { key: 'pq', name: 'Прогноз/Заказ', color: '#1baf7a' }, { key: 'pf', name: 'Прогноз/Факт', color: '#eb6834' }]
    : view === 'hyper'
    ? [{ key: 'sl', name: 'SL Факт/Заказ', color: '#2a78d6' }, { key: 'pq', name: 'План/Заказ', color: '#1baf7a' }, { key: 'pf', name: 'План/Факт', color: '#eb6834' }]
    : [{ key: 'overlaps', name: 'Перекрытия', color: '#2a78d6' }, { key: 'sick', name: 'БЛ непокр', color: '#e34948' }, { key: 'vac', name: 'ОТ непокр', color: '#eda100' }, { key: 'vacRoutes', name: 'Вакансии', color: '#4a3aa7' }];
  const chartRefs = (view === 'kpi' || isH) ? [{ y: 100, label: '100%' }, { y: 95, label: 'SL 95%' }] : [];

  return (
    <div>
      <style>{DASH_CSS}</style>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <div className={'dseg' + (view === 'disc' ? ' disc' : view === 'hyper' ? ' hyp' : '')}>
          <button className={view === 'kpi' ? 'on' : ''} onClick={() => setView('kpi')}>Показатели</button>
          <button className={view === 'disc' ? 'on' : ''} onClick={() => setView('disc')}>Дисциплина</button>
          <button className={view === 'hyper' ? 'on' : ''} onClick={() => setView('hyper')}>Гипперы</button>
        </div>
        <div className="dseg">
          <button className={periodSel === 'month' ? 'on' : ''} onClick={() => setPeriodSel('month')}>Месяц</button>
          {weekNums.map(w => <button key={w} className={periodSel === w ? 'on' : ''} onClick={() => setPeriodSel(w)}>W{w}</button>)}
        </div>
        <button onClick={() => downloadForecastRm(tmData, weekNums, rweeks)}
          style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, border: 0, background: 'var(--accent)', color: '#fff', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 700 }}>
          ⬇ Прогноз (BU/ТМ/СВ)
        </button>
        <button onClick={() => downloadRmSummary(tmData, weeks, weekNums, rweeks)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600 }}>
          ⬇ Скачать Excel
        </button>
      </div>

      {buList.length > 1 && (
        <div style={{ marginBottom: 12 }}>
          <ScopeTabs label="BU" items={[{ id: 'all', label: 'Все BU' }, ...buList.map(b => ({ id: b, label: b }))]}
                     value={buSel} onPick={id => { setBuSel(id); setTmSel('all'); setOpenTm(null); }} />
        </div>
      )}

      <SectionTitle>{buSel === 'all' ? 'Весь проект' : `BU: ${buSel}`}</SectionTitle>
      {!isH && projAgg.exits === 0 && projAgg.order === 0 && projAgg.fact === 0 && projAgg.plan === 0 && (
        <div style={{ marginBottom: 12, padding: '10px 14px', background: '#FFF9C4', border: '1px solid #F9A825', borderRadius: 8, fontSize: 12, color: '#7D5A00' }}>
          Нет данных Заказ / Прогноз / Факт за этот период. Проверьте импорт (Заказ, BI-факты) и что в БД применены миграции (колонка <b>plan_visits</b> в daily_statuses, таблицы <b>orders</b>, <b>daily_visit_facts</b>). Подробности — в консоли браузера (F12).
        </div>
      )}
      <Card style={{ marginBottom: 12 }}>
        <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 13, minWidth: 90 }}>Итого · {buTmData.length} ТМ</div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}><Tiles a={projAgg} /></div>
        </div>
      </Card>

      <TriggersCard items={(() => {
        const t = allRoutes.reduce((s, r) => s + (otAlert?.byRoute?.[r.id] || 0), 0);
        const rc = allRoutes.reduce((s, r) => s + (otAlert?.byRoute?.[r.id] ? 1 : 0), 0);
        const items = [];
        if (t > 0) {
          let first = null;
          buTmData.some(({ tm, svGroups }) => svGroups.some(g => { const r = g.routes.find(x => otAlert?.byRoute?.[x.id]); if (r) { first = { tmId: tm.id, goto: { svId: g.sv.id, tab: 'tabel', routeId: r.id } }; return true; } return false; }));
          items.push({ level: 'warn', title: 'ОТ без подтверждённого отпуска', detail: `${t} дн. по ${rc} маршрутам — в табеле стоит отпуск, но он не запланирован/не подтверждён`,
            go: (first && onGo) ? () => onGo(first) : null });
        }
        // Остальные алёрты: по одной строке на тип и ТМ, «Перейти» открывает первый (самый срочный) случай
        const TYPE = { vac: 'Заявки на отпуск ждут дольше 3 дней', cover: 'БЛ и ОТ без перекрытия в ближайшие дни', nostat: 'СВ без статусов за вчера', seen: 'СВ давно не заходили в систему', vacancy: 'Вакансии дольше 14 дней', plan: 'Низкая точность планирования' };
        const tmIds = new Set(buTmData.map(t => t.tm.id));
        const agg2 = {};
        alerts.filter(a => tmIds.has(a.tmId)).forEach(a => {
          const type = a.id.split(':')[0];
          const k = type + '|' + a.tmId;
          (agg2[k] ||= { type, tmName: a.tmName, list: [] }).list.push(a);
        });
        Object.values(agg2).forEach(({ type, tmName, list }) => {
          const worst = list[0];
          items.push({
            level: list.some(x => x.level === 'bad') ? 'bad' : 'warn',
            title: `${TYPE[type] || worst.title} — ${list.length}`,
            detail: `${tmName} · ${worst.detail}`,
            go: onGo ? () => onGo(worst) : null,
          });
        });
        const order = { bad: 0, warn: 1, info: 2 };
        return items.sort((a, b) => order[a.level] - order[b.level]);
      })()} />

      <Card style={{ marginBottom: 12 }}>
        <div style={{ padding: '12px 14px' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 10 }}>
            Динамика по неделям · {view === 'kpi' ? 'показатели' : view === 'hyper' ? 'гипперы' : 'дисциплина'}
          </div>
          <ScopeTabs label="ТМ" value={effTmSel} onPick={setTmSel}
                     items={[{ id: 'all', label: buSel === 'all' ? 'Весь проект' : 'Весь BU' }, ...buTmData.map(t => ({ id: t.tm.id, label: t.tm.full_name }))]} />
          <DynamicsChart data={wser} series={chartSeries} unit={(view === 'kpi' || isH) ? '%' : ''} refs={chartRefs} />
          <div style={{ marginTop: 12 }}><WeekStrip data={wser} view={view} activeRoutes={activeRoutes} /></div>
          <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-muted)' }}>
            Показан: <b style={{ color: 'var(--text)' }}>{chartLabel}</b> · {scopedTm
              ? <>{scopedTm.svGroups.length} СВ · {activeRoutes} рабочих маршрутов{chartRoutes.length - activeRoutes > 0 && <span> · {chartRoutes.length - activeRoutes} вакансий</span>}</>
              : <>{buTmData.length} ТМ · {buTmData.reduce((s, t) => s + t.svGroups.length, 0)} СВ · {activeRoutes} рабочих маршрутов{allRoutes.length - activeRoutes > 0 && <span> · {allRoutes.length - activeRoutes} вакансий</span>}</>}
          </div>
        </div>
      </Card>

      <SectionTitle>По каждому ТМ{buSel !== 'all' && ` · ${buSel}`}</SectionTitle>
      {buTmData.map(({ tm, svGroups }) => {
        const tmRoutes = svGroups.flatMap(g => g.routes);
        const a = agg(tmRoutes);
        const isOpen = openTm === tm.id;
        return (
          <Card key={tm.id} style={{ marginBottom: 8 }}>
            <div onClick={() => setOpenTm(isOpen ? null : tm.id)} style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', cursor: 'pointer', background: 'var(--surface2)' }}>
              <div><div style={{ fontWeight: 700, fontSize: 13 }}>{tm.full_name}</div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{svGroups.length} СВ · {tmRoutes.length} маршрутов</div></div>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}><Tiles a={a} /><span style={{ color: 'var(--text-muted)', transform: isOpen ? 'rotate(90deg)' : 'none' }}>▶</span></div>
            </div>
            {isOpen && (
              <div style={{ padding: '4px 14px 10px' }}>
                {svGroups.map(({ sv, routes }) => {
                  const sa = agg(routes);
                  return isH
                    ? <HyperMetricRow key={sv.id} name={`${sv.full_name} · ${sa.points} точек`} a={sa} />
                    : <RouteMetricRow key={sv.id} name={`${sv.full_name} · ${routes.length} марш.`} m={sa} view={view} />;
                })}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

// ── РЕДАКТОР КОНКРЕТНОГО СВ ДЛЯ РМ (аналог SvDrillDown у ТМ) ──────────────────
function RmSvEditor({ svId, svGroups, vacations, period, weeks, weekNums, rmUserId, onBack, onUpdateGroups, onUpdateVacations, visitFacts = [], focus = null }) {
  const [canHide, setCanHide] = useState(false);
  const [hidKey, setHidKey] = useState(0);
  useEffect(() => { canHideRoutes(rmUserId).then(setCanHide); }, [rmUserId]);
  const group = svGroups.find(g => g.sv.id === svId);
  if (!group) return null;

  const svVacations = vacations.filter(v => group.routes.concat(group.pmRoutes || []).some(r => r.id === v.route_id));

  function updateGroupRoutes(updater) {
    onUpdateGroups(prev => prev.map(g => g.sv.id === svId ? { ...g, routes: updater(g.routes) } : g));
  }
  function updateGroupStatuses(routeId, date, code) {
    onUpdateGroups(prev => prev.map(g => {
      if (g.sv.id !== svId) return g;
      const next = { ...g.statuses };
      if (!next[routeId]) next[routeId] = {};
      if (code === null) delete next[routeId][date];
      else next[routeId] = { ...next[routeId], [date]: code };
      return { ...g, statuses: next };
    }));
  }

  return (
    <div>
      <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: 'var(--accent)', fontSize: 13, fontWeight: 600, marginBottom: 12, cursor: 'pointer' }}>
        ← Назад к СВ
      </button>
      <SectionTitle>{group.sv.full_name}</SectionTitle>

      <EditableTabell
        pmRoutes={group.pmRoutes || []}
        onHideRoute={canHide ? async (route) => {
          await hideRoute(route.id, true, rmUserId);
          updateGroupRoutes(rs => rs.filter(r => r.id !== route.id));
          setHidKey(x => x + 1);
        } : null}
        period={period}
        weeks={weeks}
        weekNums={weekNums}
        routes={group.routes}
        statuses={group.statuses}
        vacations={svVacations}
        visitFacts={visitFacts.filter(vf => group.routes.some(r => r.id === vf.route_id))}
        focus={focus}
        onSetStatus={async ({ routeId, date, code }) => {
          if (code === null) await clearStatus({ periodId: period.id, routeId, date });
          else await setStatus({ periodId: period.id, routeId, date, statusCode: code, userId: rmUserId });
        }}
        onLocalStatusChange={(routeId, date, code) => updateGroupStatuses(routeId, date, code)}
        onSetVacation={async ({ routeId, dateFrom, dateTo, workingDays }) => {
          await setVacation({ periodId: period.id, routeId, dateFrom, dateTo, workingDays, userId: rmUserId, isTmAction: true });
        }}
        onLocalVacationChange={(routeId, vac) => {
          onUpdateVacations(prev => {
            const filtered = prev.filter(v => v.route_id !== routeId);
            return vac ? [...filtered, { route_id: routeId, status: 'approved', ...vac }] : filtered;
          });
        }}
        onDeleteVacation={async (routeId) => {
          await deleteVacation(period.id, routeId);
        }}
        onCloseVacancy={async (routeId, fullName) => {
          await closeVacancyByRoute(routeId, fullName);
        }}
        onApprove={async (vacationId) => {
          const updated = await approveVacation(vacationId, rmUserId);
          onUpdateVacations(prev => prev.map(v => v.id === vacationId ? updated : v));
        }}
        onReject={async (vacationId) => {
          const updated = await rejectVacation(vacationId, rmUserId);
          onUpdateVacations(prev => prev.map(v => v.id === vacationId ? updated : v));
        }}
        onLocalRouteChange={(routeId, patch) => {
          updateGroupRoutes(routes => routes.map(r => r.id === routeId ? { ...r, ...patch } : r));
        }}
      />
      {canHide && <HiddenRoutes svId={svId} actorId={rmUserId} refreshKey={hidKey}
        onRestored={() => window.location.reload()} />}
    </div>
  );
}
